import { FormFieldMixin } from '@schmancy/mixins/formField.mixin'
import { SchmancyElement, schmancyElement } from '@schmancy/mixins/index'
import { css, html, nothing, PropertyValues } from 'lit'
import { queryAssignedElements } from 'lit/decorators.js'
import { when } from 'lit/directives/when.js'
import { fromEvent, takeUntil } from 'rxjs'
import type { SchmancyChoice } from './choice'
import '@schmancy/primitives/typography/typography'

export type SchmancyChooseChangeEvent = CustomEvent<{ value: string }>

@schmancyElement('schmancy-choose')
export class SchmancyChoose extends FormFieldMixin(SchmancyElement) {
	static styles = [
		css`
			:host {
				display: block;
			}
		`,
	]

	// `name`, `value`, `required`, `disabled`, `label`, `error`, `validationMessage` — all from the mixin.

	@queryAssignedElements({ flatten: true, selector: 'schmancy-choice' })
	private choices!: SchmancyChoice[]

	override connectedCallback() {
		super.connectedCallback()

		this.internals.role = 'radiogroup'
		// See choice.ts's identical pairing: internals-only ARIA reflection is
		// invisible to non-Chromium AT and to attribute-reading tooling, so the
		// literal attribute carries the same value.
		this.setAttribute('role', 'radiogroup')

		fromEvent<CustomEvent<{ value: string }>>(this, 'schmancy-choice-select')
			.pipe(takeUntil(this.disconnecting))
			.subscribe(e => {
				e.stopPropagation()
				// A choice made is entry finished — there is no "still picking" the
				// way there is a "still typing", so the field is touched here rather
				// than on a blur that may never come.
				this.markTouched()
				this.value = e.detail.value
				this.syncChildren()
				this.emitChange({ value: this.value })
			})

		fromEvent<KeyboardEvent>(this, 'keydown')
			.pipe(takeUntil(this.disconnecting))
			.subscribe(e => {
				const nonDisabled = this.choices.filter(c => !c.disabled)
				if (nonDisabled.length === 0) return
				const rawIdx = nonDisabled.indexOf(document.activeElement as SchmancyChoice)
				// When focus is outside the group, ArrowDown lands on the first (treat as -1 → next is 0);
				// ArrowUp lands on the last (treat as N → previous is N-1).

				switch (e.key) {
					case 'ArrowDown':
					case 'ArrowRight': {
						e.preventDefault()
						const next = nonDisabled[(rawIdx + 1) % nonDisabled.length]
						this.moveFocus(document.activeElement as SchmancyChoice, next)
						break
					}
					case 'ArrowUp':
					case 'ArrowLeft': {
						e.preventDefault()
						const lookupIdx = rawIdx === -1 ? nonDisabled.length : rawIdx
						const prev = nonDisabled[(lookupIdx - 1 + nonDisabled.length) % nonDisabled.length]
						this.moveFocus(document.activeElement as SchmancyChoice, prev)
						break
					}
					case 'Home': {
						e.preventDefault()
						this.moveFocus(document.activeElement as SchmancyChoice, nonDisabled[0])
						break
					}
					case 'End': {
						e.preventDefault()
						this.moveFocus(document.activeElement as SchmancyChoice, nonDisabled[nonDisabled.length - 1])
						break
					}
				}
			})
	}

	override firstUpdated(changed: PropertyValues) {
		super.firstUpdated(changed)
		this.choices.forEach((c, i) => {
			if (!c.id) c.id = `${this.id || 'schmancy-choose'}-choice-${i}`
			c.tabIndex = -1
		})
		this.syncChildren()
	}

	override updated(changed: Map<string, unknown>) {
		super.updated(changed)
		if (changed.has('value')) {
			this.syncChildren()
		}
	}

	/** The `aria-label` this group last wrote from its own `label`, so it never mistakes a consumer's for it. */
	#nameFromLabel: string | null = null

	override willUpdate(changed: PropertyValues) {
		super.willUpdate(changed)
		if (changed.has('label') && this.internals) {
			this.internals.ariaLabel = this.label || null
			// This host IS the radiogroup, so a consumer's aria-label already names
			// it where it is read, and outranks `label`: only a name this group
			// wrote itself is replaced or taken away.
			const current = this.getAttribute('aria-label')
			if (current !== null && current !== this.#nameFromLabel) return
			if (this.label) this.setAttribute('aria-label', this.label)
			else this.removeAttribute('aria-label')
			this.#nameFromLabel = this.label || null
		}
	}

	override resetForm() {
		super.resetForm()
		this.syncChildren()
	}

	private moveFocus(from: SchmancyChoice | null, to: SchmancyChoice) {
		if (from && this.choices.includes(from)) from.tabIndex = -1
		to.tabIndex = 0
		to.focus()
	}

	private syncChildren() {
		const current = this.value
		const firstNonDisabled = this.choices.find(c => !c.disabled) ?? null
		const active = this.choices.find(c => c.value === current) ?? firstNonDisabled
		this.choices.forEach(c => {
			c.selected = c.value === current
			c.tabIndex = c === active ? 0 : -1
		})
	}

	render() {
		return html`
			<div
				aria-invalid=${this.error ? 'true' : 'false'}
				class=${
					this.error
						? 'grid grid-cols-1 gap-2 rounded-lg outline-solid outline-2 outline-error-default p-2'
						: 'grid grid-cols-1 gap-2'
				}
			>
				${when(
					this.label,
					() => html`<schmancy-typography type="label" token="lg">${this.label}</schmancy-typography>`,
				)}
				<slot></slot>
			</div>
			${
				this.error && this.validationMessage
					? html`<div role="alert" class="text-error-default text-xs mt-1">${this.validationMessage}</div>`
					: nothing
			}
		`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-choose': SchmancyChoose
	}
}
